import type { AuthTokenClaims } from "@/stores/useAuthClaimsStore";

export const VERIFY_EMAIL_BANNER_EVENT = "verify-email-banner:show";

const DISMISS_KEY = "verifyEmailBannerDismissed";

export const isBannerDismissed = (): boolean => {
  if (typeof window === "undefined") return false;
  return sessionStorage.getItem(DISMISS_KEY) === "true";
};

export const setBannerDismissed = () => {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(DISMISS_KEY, "true");
};

export const clearBannerDismissed = () => {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(DISMISS_KEY);
};

export const showVerifyEmailBanner = () => {
  if (typeof window === "undefined") return;
  localStorage.setItem("showVerifyEmailBanner", "true");
  window.dispatchEvent(new Event(VERIFY_EMAIL_BANNER_EVENT));
};

export const clearVerifyEmailBanner = () => {
  if (typeof window === "undefined") return;
  localStorage.removeItem("showVerifyEmailBanner");
  window.dispatchEvent(new Event(VERIFY_EMAIL_BANNER_EVENT));
};

export const isVerified = (claims?: AuthTokenClaims): boolean => {
  if (!claims?.permissions) return false;
  return claims.permissions.includes("read:orders") || claims.permissions.includes("read:tax");
};

const AUTH0_ISSUER_HOSTNAMES = new Set(["staging.auth.stickergiant.com", "auth.stickergiant.com"]);

export const isReturningFromAuth0 = (searchParams: URLSearchParams) => {
  const issuer = searchParams.get("iss");
  if (!issuer) return false;

  try {
    const issuerUrl = new URL(issuer);
    return AUTH0_ISSUER_HOSTNAMES.has(issuerUrl.hostname);
  } catch {
    return false;
  }
};

export const shouldShowBanner = (onReturnFromAuth0?: () => void) => {
  if (typeof window === "undefined") return false;

  const url = new URL(window.location.href);
  const { searchParams } = url;

  if (isReturningFromAuth0(searchParams)) {
    localStorage.removeItem("showVerifyEmailBanner");
    searchParams.delete("iss");
    window.history.replaceState({}, document.title, url.toString());
    onReturnFromAuth0?.();
    return false;
  }

  const showBanner =
    searchParams.get("error") === "access_denied" &&
    searchParams.get("error_description")?.toLowerCase().includes("verify your email");
  if (showBanner) {
    localStorage.setItem("showVerifyEmailBanner", "true");

    url.searchParams.delete("error");
    url.searchParams.delete("error_description");
    url.searchParams.delete("state");
    window.history.replaceState({}, document.title, url.toString());
    return true;
  }

  return localStorage.getItem("showVerifyEmailBanner") === "true";
};
